import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
  modalContainerStyle?: React.CSSProperties
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  modalContainerStyle,
}) => {
  // Disable page scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300"
      onClick={onClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-md" />

      {/* Modal */}
      <div
        className="bg-[var(--panel-bg)] rounded-lg max-w-3xl w-full max-h-[70vh] overflow-hidden flex flex-col relative z-10 transform transition-all duration-300 scale-100 opacity-100"
        style={modalContainerStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <h4 className="active-gradient text-[var(--balance-text-color)] font-semibold text-lg px-6 py-2 rounded-t-lg w-full">
          {title}
        </h4>

        {/* Content */}
        <div className="px-10 py-8 space-y-6 overflow-y-auto custom-scrollbar">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-[var(--panel-border)] p-4 flex justify-end bg-[var(--panel-bg)]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

export default Modal
